import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider, useSettings } from "@/context/SettingsContext";
import { useEffect, lazy, Suspense } from "react";
import axios from "axios";
import FeedbackButton from "@/components/FeedbackButton";
import { ErrorBoundary } from "@/components/Skeletons";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

const ConfiguratorPage = lazy(() => import("@/pages/ConfiguratorPage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const CatalogPage = lazy(() => import("@/pages/PortfolioPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const ContactsPage = lazy(() => import("@/pages/ContactsPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const CarDetailPage = lazy(() => import("@/pages/CarDetailPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AppContent() {
  useEffect(() => {
    axios.post(`${API}/seed`).catch(() => {});
  }, []);

  const settings = useSettings();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin") || location.pathname === "/login";

  return (
    <div className="min-h-screen bg-[#050505] text-[#E5E5E5]">
      <Navbar />
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/cars/:slug" element={<CarDetailPage />} />
          <Route path="/podbor" element={<ConfiguratorPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/admin/*" element={<AdminPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/portfolio" element={<Navigate to="/catalog" replace />} />
          <Route path="/configurator" element={<Navigate to="/podbor" replace />} />
          <Route path="*" element={
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-6xl font-light text-[#D4AF37] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>404</h1>
                <p className="text-[#A3A3A3] mb-6">Страница не найдена</p>
                <a href="/" className="text-sm text-[#D4AF37] border border-[#D4AF37]/30 px-6 py-2 hover:bg-[#D4AF37]/10 transition-colors">На главную</a>
              </div>
            </div>
          } />
        </Routes>
        </Suspense>
      </ErrorBoundary>
      <Footer />
      {!isAdmin && <FeedbackButton settings={settings} />}
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </SettingsProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
